/**
 * Table logic, with no React in it.
 *
 * There is no React testing library in this project, and the alternative to
 * testing logic is not testing it. So the decisions a table makes — how rows
 * sort, what a search matches, what clicking a header does — live here as pure
 * functions, and `DataTable.tsx` only draws the result. This is the same split
 * the rest of the codebase uses: `computeDocument` is pure and tested, the
 * editor consumes it.
 *
 * THE EDGE CASES ARE THE POINT. A table that crashes on a null cell, or that
 * sorts `10` before `9`, or that mutates the caller's array while sorting it, is
 * worse than no table — it looks like it works. Every one of those is a test.
 */
import type { ReactNode } from 'react';

/** Which way a column is sorted. */
export type SortDir = 'asc' | 'desc';

/** What a table column knows about itself. */
export interface Column<T> {
  /** Stable identity, used as the sort key. */
  readonly key: string;
  readonly header: string;
  /**
   * Read the sortable value out of a row.
   *
   * A function rather than a property path, because a path cannot express "the
   * customer's name" and cannot cope with a nested or missing field. Returning
   * `null`/`undefined` is expected and handled, not exceptional.
   */
  readonly value: (row: T) => string | number | null | undefined;
  /** Override the default comparison — for dates, money, or anything ordinal. */
  readonly compare?: (a: T, b: T) => number;
  /** Right-alignment is the default for money; this overrides the guess. */
  readonly align?: 'left' | 'right';
  /** Rendered instead of the raw value, for links and badges. */
  readonly render?: (row: T) => ReactNode;
}

/** The current sort, as a table holds it. `key: null` means unsorted. */
export interface SortState {
  readonly key: string | null;
  readonly dir: SortDir;
}

export const UNSORTED: SortState = { key: null, dir: 'asc' };

/**
 * Compare two cell values.
 *
 * Numbers compare numerically, so `9` sorts before `10` — comparing them as
 * strings is the classic table bug and it is invisible until a shopkeeper has
 * nine of something.
 *
 * An absent value sorts AFTER a present one, in both directions. A row with no
 * value is not "smaller than" everything; putting a run of blanks at the top of
 * a list reads as if those rows matter most.
 */
export function compareValues(
  a: string | number | null | undefined,
  b: string | number | null | undefined,
): number {
  const aMissing = a === null || a === undefined;
  const bMissing = b === null || b === undefined;
  if (aMissing && bMissing) return 0;
  if (aMissing) return 1;
  if (bMissing) return -1;

  if (typeof a === 'number' && typeof b === 'number') return a - b;

  // Case-insensitive, and locale-aware: 'a' before 'B', not after it. A list
  // of customers sorted so that every capitalised name follows every lowercase
  // one looks broken even when it is technically ordered.
  return String(a).localeCompare(String(b), undefined, {
    numeric: true,
    sensitivity: 'base',
  });
}

/**
 * Sort rows by one column.
 *
 * Does NOT mutate the input — a table that reorders the caller's array as a side
 * effect of drawing it corrupts every other view of the same data.
 *
 * An unknown `key` returns the rows as they came. That happens when a column is
 * removed while a stale sort is still selected, and the correct answer is "leave
 * it alone", not a blank screen.
 */
export function sortRows<T>(
  rows: readonly T[],
  columns: readonly Column<T>[],
  key: string | null,
  dir: SortDir,
): T[] {
  if (key === null) return [...rows];
  const column = columns.find((c) => c.key === key);
  if (!column) return [...rows];

  const sign = dir === 'desc' ? -1 : 1;
  // A copy first, so the input is untouched. Array.prototype.sort is stable in
  // every engine this app targets, so equal keys keep their original order.
  return [...rows].sort((a, b) => {
    if (column.compare) return sign * column.compare(a, b);
    return sign * compareValues(column.value(a), column.value(b));
  });
}

/**
 * Does a row match what the user typed?
 *
 * Matches ANY of the given columns, so one search box covers name, code and
 * GSTIN. An empty query matches everything — otherwise clearing the box would
 * empty the table, which reads as "the search broke".
 */
export function matchesQuery<T>(
  row: T,
  query: string,
  columns: readonly Column<T>[],
): boolean {
  const q = query.trim().toLowerCase();
  if (q === '') return true;
  return columns.some((column) => {
    const value = column.value(row);
    if (value === null || value === undefined) return false;
    return String(value).toLowerCase().includes(q);
  });
}

/** The rows matching a search, order preserved. */
export function filterRows<T>(
  rows: readonly T[],
  query: string,
  columns: readonly Column<T>[],
): T[] {
  if (query.trim() === '') return [...rows];
  return rows.filter((row) => matchesQuery(row, query, columns));
}

/**
 * What happens when a header is clicked.
 *
 * Clicking the column already sorted flips the direction; clicking a different
 * column starts ascending. Returning the state rather than mutating it keeps
 * React's change detection honest.
 */
export function toggleSort(current: SortState, key: string): SortState {
  if (current.key !== key) return { key, dir: 'asc' };
  return { key, dir: current.dir === 'asc' ? 'desc' : 'asc' };
}

/** The `aria-sort` value for a column, which screen readers actually use. */
export function ariaSortFor(sort: SortState, key: string): 'ascending' | 'descending' | 'none' {
  if (sort.key !== key) return 'none';
  return sort.dir === 'asc' ? 'ascending' : 'descending';
}
